import { getAllergyIntolerancesById } from '@ctw/shared/api/fhir/allergies';
import { fetchConditionsByIdFQS } from '@ctw/shared/api/fhir/conditions';
import { fetchDiagnosticReportsFromFQSById } from '@ctw/shared/api/fhir/diagnostic-report';
import { getDocumentReferenceWithBasicsById } from '@ctw/shared/api/fhir/document';
import { getMedicationStatementsByIdFQS } from '@ctw/shared/api/fhir/medications';
import type { AllergyModel } from '@ctw/shared/api/fhir/models/allergies';
import type { ConditionModel } from '@ctw/shared/api/fhir/models/condition';
import type { DiagnosticReportModel } from '@ctw/shared/api/fhir/models/diagnostic-report';
import type { DocumentModel } from '@ctw/shared/api/fhir/models/document';
import type { MedicationStatementModel } from '@ctw/shared/api/fhir/models/medication-statement';
import type { ObservationModel } from '@ctw/shared/api/fhir/models/observation';
import type { PatientModel } from '@ctw/shared/api/fhir/models/patient';
import { fetchObservationsById } from '@ctw/shared/api/fhir/observations';
import { LENS_EXTENSION_ID } from '@ctw/shared/api/fhir/system-urls';
import { usePatientQuery } from '@ctw/shared/context/patient-provider';
import { getApiServiceBaseUrl } from '@ctw/shared/utils/environments';
import { QUERY_KEY_AI_SEARCH } from '@ctw/shared/utils/query-keys';
import type { UseQueryResult } from '@tanstack/react-query';
import type { Resource } from 'fhir/r4';
import type { GraphQLClient } from 'graphql-request';
import { groupBy, keyBy, mapValues, uniq } from 'lodash-es';

type PatientRecordSearchResourceType =
  | 'AllergyIntolerance'
  | 'Condition'
  | 'DocumentReference'
  | 'MedicationStatement'
  | 'Observation'
  | 'DiagnosticReport';

type ResourceByTypeMapping = Record<
  PatientRecordSearchResourceType,
  Array<PatientRecordSearchResponseRawDocument>
>;
type ResourceIdByTypeMapping = Record<PatientRecordSearchResourceType, Array<string>>;

export type PatientRecordSearchResponseRawDocument = {
  original_content?: Resource;
  page_content: string;
  summary: string;
  reason: {
    // highlighting behavior on N spans in page_content
    search_type: Array<string>; // origination of what the search engine did that surfaced this information
    span: Array<{ begin: number; end: number }>;
  };
  metadata: {
    resource_id: string;
    resource_type: PatientRecordSearchResourceType;
    subject: string;
    upid: string;
  };
};

export type PatientRecordSearchResponseRaw = {
  documents: Array<PatientRecordSearchResponseRawDocument>;
  query: string;
  query_id: string;
  response: string;
  service_version: string;
};

export type PatientRecordSearchResult = {
  page_content: string;
  upid: string;
  document: PatientRecordSearchResponseRawDocument & {
    resource?:
      | AllergyModel
      | ConditionModel
      | DocumentModel
      | MedicationStatementModel
      | ObservationModel
      | DiagnosticReportModel;
  };
};

export type PatientRecordSearchResults = {
  id: string;
  results: ReadonlyArray<PatientRecordSearchResult>;
  query: string;
  response: string;
  total: number;
};

export const EMPTY_SEARCH_RESULTS: PatientRecordSearchResults = {
  id: '',
  query: '',
  response: '',
  results: [] as Array<PatientRecordSearchResult>,
  total: 0,
};

const EMPTY_RESOURCE_BY_TYPE_MAPPING: ResourceByTypeMapping = {
  AllergyIntolerance: [],
  Condition: [],
  DocumentReference: [],
  MedicationStatement: [],
  DiagnosticReport: [],
  Observation: [],
};

class PatientRecordSearch {
  private response: PatientRecordSearchResponseRaw;

  private resources: {
    AllergyIntolerance: Record<string, AllergyModel>;
    Condition: Record<string, ConditionModel>;
    DocumentReference: Record<string, DocumentModel>;
    MedicationStatement: Record<string, MedicationStatementModel>;
    DiagnosticReport: Record<string, DiagnosticReportModel>;
    Observation: Record<string, ObservationModel>;
  } = {
    AllergyIntolerance: {},
    Condition: {},
    DocumentReference: {},
    MedicationStatement: {},
    DiagnosticReport: {},
    Observation: {},
  };

  public constructor(patientRecordSearchResponse: PatientRecordSearchResponseRaw) {
    this.response = patientRecordSearchResponse;
  }

  public get id(): string {
    return this.response.query_id;
  }

  public get results(): Array<PatientRecordSearchResult> {
    return this.response.documents.map((group) => {
      const resource = this.resources[group.metadata.resource_type][group.metadata.resource_id];
      return {
        upid: group.metadata.upid,
        page_content: group.page_content,
        document: {
          ...group,
          resource,
        },
      };
    });
  }

  public get filteredResults(): Array<PatientRecordSearchResult> {
    return this.results.filter((result) => {
      if (['Condition', 'MedicationStatement'].includes(result.document.metadata.resource_type)) {
        const extensions = result.document.resource?.resource.extension ?? [];
        return extensions.some(({ url }) => url === LENS_EXTENSION_ID);
      }
      return !!result.document.resource;
    });
  }

  public get queryString(): string {
    return this.response.query;
  }

  public get responseString(): string {
    return this.response.response;
  }

  // Get a Map of all resource IDs from the search result by resource type
  public get resourceIds(): ResourceIdByTypeMapping {
    const resourcesByTypePartial: Partial<ResourceByTypeMapping> = groupBy(
      this.response.documents,
      (r) => r.metadata.resource_type,
    );

    const resourcesByType: ResourceByTypeMapping = {
      ...EMPTY_RESOURCE_BY_TYPE_MAPPING,
      ...resourcesByTypePartial,
    };

    return mapValues(resourcesByType, (docs) =>
      docs.map((resource) => resource.metadata.resource_id),
    );
  }

  // Set the resource models to their search results
  public setResources(params: {
    allergies: Array<AllergyModel>;
    conditions: Array<ConditionModel>;
    documents: Array<DocumentModel>;
    medications: Array<MedicationStatementModel>;
    diagnosticReports: Array<DiagnosticReportModel>;
    observations: Array<ObservationModel>;
  }) {
    this.resources.AllergyIntolerance = keyBy(params.allergies, 'id');
    this.resources.Condition = keyBy(params.conditions, 'id');
    this.resources.DocumentReference = keyBy(params.documents, 'id');
    this.resources.MedicationStatement = keyBy(params.medications, 'id');
    this.resources.DiagnosticReport = keyBy(params.diagnosticReports, 'id');
    this.resources.Observation = keyBy(params.observations, 'id');
  }
}

/*
 * @deprecated This should not be used and will be replaced with packages/shared/api/patient-record-search/patient-record-search-fqs.ts
 */
export const usePatientRecordSearch = (
  searchTerm: string,
): UseQueryResult<PatientRecordSearchResults> =>
  usePatientQuery({
    queryId: QUERY_KEY_AI_SEARCH,
    queryKey: [searchTerm],
    queryFn: async ({ graphqlClient, telemetry, requestContext, ctwFetch, patient }) => {
      // fetchResourcesById will avoid lots of boilerplate code fetching various resources from FQS.
      const fetchResourcesById = async <T>(
        ids: Array<string>,
        fn: (
          t: typeof telemetry,
          g: GraphQLClient,
          p: PatientModel,
          ids: Array<string>,
        ) => Promise<Array<T>>,
      ): Promise<Array<T>> =>
        ids.length === 0 ? [] : fn(telemetry, graphqlClient, patient, uniq(ids));

      if (searchTerm) {
        const searchOptions = ['keyword'];

        if (searchTerm.split(' ').length > 2) {
          searchOptions.push('semantic');
        }

        const body = JSON.stringify({
          query: searchTerm,
          upid: patient.UPID,
          include: searchOptions,
          n_results: 30,
          resource_types: [
            'AllergyIntolerance',
            'Condition',
            'DocumentReference',
            'MedicationStatement',
            'DiagnosticReport',
            // "Observation",
          ],
        });

        const { data } = await ctwFetch(
          `${getApiServiceBaseUrl({ service: 'search-poc', env: requestContext.env })}/search`,
          {
            body,
            method: 'POST',
            headers: {
              Authorization: `Bearer ${requestContext.authToken}`,
              'Content-Type': 'application/json',
              'Content-Length': body.length.toString(),
              'Zus-Account': requestContext.builderId,
            },
          },
        );

        const patientRecordSearchResult = new PatientRecordSearch(
          data as PatientRecordSearchResponseRaw,
        );

        const {
          AllergyIntolerance: allergyIds,
          Condition: conditionIds,
          DocumentReference: documentIds,
          MedicationStatement: medicationIds,
          DiagnosticReport: diagnosticReportIds,
          Observation: observationIds,
        } = patientRecordSearchResult.resourceIds;

        const allergies = await fetchResourcesById(allergyIds, getAllergyIntolerancesById);
        const conditions = await fetchResourcesById(conditionIds, fetchConditionsByIdFQS);
        const documents = await fetchResourcesById(documentIds, getDocumentReferenceWithBasicsById);
        const medications = await fetchResourcesById(medicationIds, getMedicationStatementsByIdFQS);
        const observations = await fetchResourcesById(observationIds, fetchObservationsById);
        const diagnosticReports = await fetchResourcesById(
          diagnosticReportIds,
          fetchDiagnosticReportsFromFQSById,
        );

        patientRecordSearchResult.setResources({
          allergies: allergies as Array<AllergyModel>,
          conditions,
          documents,
          medications: medications as Array<MedicationStatementModel>,
          observations: observations as Array<ObservationModel>,
          diagnosticReports,
        });

        return {
          id: patientRecordSearchResult.id,
          query: patientRecordSearchResult.queryString,
          response: patientRecordSearchResult.responseString,
          results: patientRecordSearchResult.filteredResults.sort((r) =>
            r.document.reason.search_type.includes('semantic') ? 1 : -1,
          ),
          total: patientRecordSearchResult.filteredResults.length,
        };
      }

      return EMPTY_SEARCH_RESULTS;
    },
  });
